import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import './LegalApp.css';
import Navbar from './Navbar';
import Footer from './Footer';
import mammoth from "mammoth";
import { FaFileWord } from "react-icons/fa";
import { LEGACY_FONTS, convertLegacyToUnicode, isLegacyFontSelected } from "../utils/legacyFontConverter";


const UploadStatement = ({ API_URL }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const id = location.state?.id ?? null;

  const [language, setLanguage] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isTyping, setIsTyping] = useState(true);

  const [date, setDate] = useState('');
  const [name, setName] = useState('');

  const [showOtherLanguagePopup, setShowOtherLanguagePopup] = useState(false);

  const DEFAULT_STATEMENT_TEMPLATE =
    "Date of Examination:\nName:\n\nInterrogation Response/Statement:\n";

  const [responseText, setResponseText] = useState('');
  const [legacyFont, setLegacyFont] = useState(LEGACY_FONTS[0]);
  // Holds the last raw (un-converted) statement text - either straight from
  // the Word file or straight from the user's typing/pasting - so switching
  // the Legacy Font dropdown re-converts from that original source instead
  // of re-running a conversion on text that may already be converted once.
  const rawResponseTextRef = useRef('');
  const wordFileInputRef = useRef(null);
  const [isParsingFile, setIsParsingFile] = useState(false);

  const MAX_WORD_COUNT = 1000;
  const countWords = (text) => {
    const trimmed = (text || '').trim();
    return trimmed ? trimmed.split(/\s+/).length : 0;
  };
  const responseWordCount = countWords(responseText);
  const isOverWordLimit = responseWordCount > MAX_WORD_COUNT;
  const [showWordLimitPopup, setShowWordLimitPopup] = useState(false);
  const wasOverWordLimitRef = useRef(false);

  useEffect(() => {
    if (isOverWordLimit && !wasOverWordLimitRef.current) {
      setShowWordLimitPopup(true);
    }
    wasOverWordLimitRef.current = isOverWordLimit;
  }, [isOverWordLimit]);

  const fileInputRef = useRef(null);

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  const handleNameFocus = (e) => {
    if (name === 'Witness/Accused Name') {
      setName('');
    }
  };

  const handleNameDefocus = (e) => {
    if (name.trim() === '') {
      setName('Witness/Accused Name');
    }
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    setName(value);
  };

  const handleDateChange = (e) => {
    const value = e.target.value;
    setDate(value);
  };

  const handleCancel = (e) => {
    e.preventDefault();
    navigate(-1); // Go back to the previous page
  };

  const upsertIRTemplateAndInsert = (currentText, fileContent) => {
    const template = DEFAULT_STATEMENT_TEMPLATE;
    const content = (fileContent || "").trim();

    // Match "Interrogation Response/Statement:" case-insensitive
    const irRegex = /Interrogation\s*Response\s*\/\s*Statement\s*:\s*/i;

    let text = currentText || "";

    // If heading missing, reset to default template
    if (!irRegex.test(text)) {
      text = template;
    }

    const match = irRegex.exec(text);
    if (!match) {
      // Safety fallback
      return template + "\n" + content + "\n";
    }

    const insertPos = match.index + match[0].length;

    // Replace everything AFTER the heading with the file content
    const before = text.slice(0, insertPos);
    return `${before}\n${content}\n`;
  };

  // Converts pasted text on the way in when a legacy font is selected - the
  // dropdown-driven reconversion only fires on a font *change*, so without
  // this a paste (e.g. copying a paragraph out of an old Kruti Dev document)
  // after the font was already selected would silently stay unconverted.
  const handleResponseTextPaste = (e) => {
    const pasted = (e.clipboardData || window.clipboardData).getData("text");
    if (!pasted) return;
    e.preventDefault();

    const textarea = e.target;
    const selectionStart = textarea.selectionStart;
    const selectionEnd = textarea.selectionEnd;

    const convertedPasted = isLegacyFontSelected(legacyFont) ? convertLegacyToUnicode(pasted, legacyFont) : pasted;
    const newDisplayed = responseText.slice(0, selectionStart) + convertedPasted + responseText.slice(selectionEnd);

    // Best-effort: mirror the same splice into the raw baseline using the
    // original (un-converted) pasted text, so a later font switch still
    // reconverts from the true source. Exact when raw/displayed are still
    // aligned (nothing converted yet, or pasting at the end); approximate
    // otherwise, since conversion can change text length.
    const raw = rawResponseTextRef.current;
    const rawStart = Math.min(selectionStart, raw.length);
    const rawEnd = Math.min(selectionEnd, raw.length);
    rawResponseTextRef.current = raw.slice(0, rawStart) + pasted + raw.slice(rawEnd);

    setResponseText(newDisplayed);

    const newCursorPos = selectionStart + convertedPasted.length;
    setTimeout(() => textarea.setSelectionRange(newCursorPos, newCursorPos), 0);
  };

  const handleWordFileClick = () => {
    if (language === "") {
      setErrorMessage("Please select the language to proceed.");
      return;
    }

    setErrorMessage(null);
    wordFileInputRef.current?.click();
  };

  const handleWordFileSelected = async (event) => {
    try {
      const file = event.target.files?.[0];
      if (!file) return;

      const isDocx =
        file.type ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
        file.name.toLowerCase().endsWith(".docx");

      if (!isDocx) {
        setErrorMessage("Please select a .docx Word document.");
        return;
      }

      setIsParsingFile(true);

      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });
      const extractedText = (result?.value || "").trim();

      if (!extractedText) {
        setErrorMessage("No readable text found in the selected Word document.");
        return;
      }

      //setResponseText((prev) => upsertIRTemplateAndInsert(prev, extractedText));
      rawResponseTextRef.current = extractedText;
      setResponseText(isLegacyFontSelected(legacyFont) ? convertLegacyToUnicode(extractedText, legacyFont) : extractedText);
      // Ensure typing mode (we’re filling textarea)
      if (!isTyping) setIsTyping(true);
    } catch (err) {
      console.error(err);
      setErrorMessage("Failed to read the Word document. Please try another file.");
    } finally {
      setIsParsingFile(false);
      if (wordFileInputRef.current) wordFileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    setErrorMessage(null);
    if (!isTyping && !selectedFile) {
      //alert('Please select the document to proceed.'); 
      setErrorMessage("Please select the response PDF to proceed.");
      return;
    };

    if (isTyping) {
      if (name.trim() === '') {
        setErrorMessage("Enter the Witness/Accused name.");
        return;
      }
      if (date.trim() === '') {
        setErrorMessage("Enter the date.");
        return;
      }
      if (language.trim() === '') {
        setErrorMessage("Select the language.");
        return;
      }
      if (responseText.trim() === '') {
        setErrorMessage("Enter the statement.");
        return;
      }
    }


    const header =
      `Name: ${name.trim()}\n` +
      `Date of Examination: ${date.trim()}\n\n` +
      `Statement:\n`;

    var statement = responseText;
    // check if URL starts with https://legalapp.in or https://www.legalapp.in
    if (window.location.href.startsWith('https://legalapp.in') || window.location.href.startsWith('https://www.legalapp.in')) {
      statement = header + responseText;
    }

    setIsLoading(true);
    var jwt = localStorage.getItem('jwtToken');

    const formData = new FormData();
    formData.append('id', id);
    formData.append('language', language);
    formData.append('name', name);
    formData.append('date', date);

    if (isTyping) {
      formData.append('statement', statement);
    } else {
      formData.append('file', selectedFile);
    }

    try {
      var uri = '/fir/ir-pdf';
      if (isTyping) {
        uri = '/fir/statement';
      }

      const response = await axios.post(API_URL + uri, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${jwt}`,
        },
      });
      //console.log('API Response Status:', response.status);
      //console.log('API Response:', response.data);

      if (isTyping) {
        //navigate('/fir', { state: { API_URL: API_URL, data: response.data } });
        // navigate('/investigate/fir', { state: { API_URL: API_URL, data: response.data } });
        navigate('/investigate/fir', {
          state: {
            fir: response.data,
            section: "statements"
          }
        });
      } else {
        navigate('/response/text', { state: { API_URL: API_URL, data: response.data, } });
      }
    } catch (error) {
      const defaultErrorMessage = 'Error processing request. Please try again later.';
      if (error.response?.status === 401) {
        setErrorMessage('Your session has expired. Please log in again.');
      } else {
        setErrorMessage(error.response?.data?.message || error.message || defaultErrorMessage);
      }
      console.error('Error submitting the form:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLanguageChange = (event) => {
    if (event.target.value === 'Other') {
      setShowOtherLanguagePopup(true);
    } else {
      setLanguage(event.target.value);
      setShowOtherLanguagePopup(false);
    }
  };

  const handleLanguageChangePopup = (event) => {
    setShowOtherLanguagePopup(false);
  }

  const handleTyping = () => {
    setIsTyping(!isTyping);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const toDDMMYYYY = (iso) => {
    if (!iso) return "";
    const [yyyy, mm, dd] = iso.split("-");
    return `${dd}/${mm}/${yyyy}`;
  };

  const toISO = (ddmmyyyy) => {
    const m = ddmmyyyy.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!m) return "";
    const [, dd, mm, yyyy] = m;
    return `${yyyy}-${mm}-${dd}`;
  };

  return (
    <div>
      <Navbar />

      <div className="form-container" style={{ marginTop: '100px' }}>
        <div style={{ alignContent: 'center', textAlign: 'center' }}>
          <h2>Analyze Statement</h2>
        </div>
        <br></br>
        <form
          className="responsive-form"
          onSubmit={handleSubmit}
          noValidate
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",   // key: stop centering
            justifyContent: "flex-start"
          }}
        >

          <style>
            {`
  .date-input::placeholder {
    color: black;   /* same grey as Name input */
    opacity: 1;       /* ensures consistency across browsers */
  }
`}
          </style>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
            <div
              style={{
                width: "100%",
                display: "flex",
                gap: "16px",
                alignItems: "flex-end",
                justifyContent: "space-between",
                flexWrap: "nowrap",          // keep 3 in one row
                boxSizing: "border-box"
              }}
            >
              <div style={{ flex: "1 1 0", minWidth: 0 }}>
                <label style={{ textAlign: "left" }}>Name</label>
                <input
                  type="text"
                  name="name"
                  value={name}
                  onChange={handleNameChange}
                  onFocus={handleNameFocus}
                  onBlur={handleNameDefocus}
                  required
                  className="upload-input"
                  placeholder="Witness/Accused Name"
                  style={{ width: "100%", boxSizing: "border-box" }}
                />
              </div>
              <div style={{ flex: "1 1 0", minWidth: 0 }}>
                <label style={{ textAlign: "left" }}>Examination Date</label>
                <input
                  type="date"
                  name="date"
                  value={toISO(date)} // date stored as dd/MM/yyyy in state
                  onChange={(e) => setDate(toDDMMYYYY(e.target.value))}
                  required
                  style={{ width: "100%", boxSizing: "border-box" }}
                />
              </div>
              <div style={{ flex: "1 1 0", minWidth: 0 }}>
                <label htmlFor="document-type">Statement Language</label>
                <select
                  id="document-type"
                  name="document-type"
                  className="upload-select"
                  onChange={handleLanguageChange}
                  value={language}
                  style={{ width: "100%", boxSizing: "border-box" }}
                >
                  <option value="" disabled>
                    Select Language
                  </option>

                  <option value="English">English</option>
                  <option value="Hindi">Hindi</option>
                  <option value="Gujarati">Gujarati</option>
                  <option value="Marathi">Marathi</option>
                  <option value="Bengali">Bengali</option>
                  <option value="Odia">Odia</option>
                  <option value="Telugu">Telugu</option>
                  <option value="Tamil">Tamil</option>
                  <option value="Kannada">Kannada</option>
                  <option value="Malayalam">Malayalam</option>
                  <option value="Punjabi">Punjabi</option>
                  <option value="Urdu">Urdu</option>
                </select>
              </div>
            </div>

            <div style={{ width: "100%", maxWidth: "260px", marginTop: "16px" }}>
              <label htmlFor="legacy-font">Legacy Font</label>
              <select
                id="legacy-font"
                name="legacy-font"
                className="upload-select"
                value={legacyFont}
                onChange={(e) => {
                  const newFont = e.target.value;
                  setLegacyFont(newFont);
                  if (isLegacyFontSelected(newFont) && rawResponseTextRef.current.trim() !== "") {
                    setResponseText(convertLegacyToUnicode(rawResponseTextRef.current, newFont));
                  } else if (newFont === LEGACY_FONTS[0]) {
                    // Not Applicable - restore the original text as first pasted/extracted, undoing any conversion.
                    setResponseText(rawResponseTextRef.current);
                  }
                }}
                style={{ width: "100%", boxSizing: "border-box" }}
              >
                {LEGACY_FONTS.map((font) => (
                  <option key={font} value={font}>{font}</option>
                ))}
              </select>
            </div>

            <br></br><br></br>

            <input
              ref={wordFileInputRef}
              type="file"
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              style={{ display: "none" }}
              onChange={handleWordFileSelected}
            />

            {isTyping && (
              <div className="form-group" style={{ position: "relative", width: "100%" }}>
                <label htmlFor="document">Statement</label>

                <textarea
                  value={responseText}
                  onChange={(e) => {
                    setResponseText(e.target.value);
                    rawResponseTextRef.current = e.target.value;
                  }}
                  onPaste={handleResponseTextPaste}
                  disabled={isParsingFile}
                  style={{
                    width: "99%",
                    height: "300px",
                    marginTop: "5px",
                    fontSize: "16px",
                    lineHeight: "1.5",
                    resize: "vertical",
                    opacity: isParsingFile ? 0.5 : 1,
                  }}
                />

                <p style={{ marginTop: "8px", fontSize: "13px", textAlign: "right", color: isOverWordLimit ? "#d32f2f" : "#666" }}>
                  {responseWordCount} / {MAX_WORD_COUNT} words
                </p>

                {isParsingFile && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      backgroundColor: "rgba(255, 255, 255, 0.6)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 5,
                      borderRadius: "4px",
                    }}
                  >
                    <div style={{ textAlign: "center" }}>
                      <div className="spinner" />
                      <p style={{ marginTop: "8px", fontWeight: 600 }}>
                        Reading Word document…
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={{ width: "100%" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                marginTop: "20px",
                position: "relative",
                gap: "12px",
              }}
            >
              {/* File Button (Left) */}
              <button
                type="button"
                onClick={handleWordFileClick}
                disabled={isLoading || isParsingFile}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "10px 14px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                  background: "white",
                  cursor:
                    isLoading || isParsingFile
                      ? "not-allowed"
                      : "pointer",
                  fontWeight: 600,
                }}
              >
                <FaFileWord style={{ fontSize: "18px", color: "#2B579A" }} />
                File
              </button>

              {/* Submit Button (Center) */}
              <button
                type="submit"
                className="submit-button"
                style={{ fontWeight: "bold" }}
                disabled={
                  isLoading ||
                  isParsingFile ||
                  (!isTyping && !selectedFile) ||
                  (isTyping && isOverWordLimit)
                }
                hidden={errorMessage}
              >
                {isLoading ? <div className="spinner"></div> : "Submit"}
              </button>

              {/* Cancel Button (Right) */}
              <button
                type="button"
                onClick={handleCancel}
                style={{
                  backgroundColor: "transparent",
                  border: "none",
                  borderBottom: "1px solid #000000",
                  padding: "4px 0 2px 0",
                  fontWeight: "bold",
                  cursor: "pointer",
                  marginRight: "20px",
                }}
                disabled={isLoading || isParsingFile}
              >
                Cancel
              </button>
            </div>

            {/* Loading Message for server submit */}
            <div hidden={!isLoading} style={{ marginTop: "10px", textAlign: "center" }}>
              <p style={{ fontSize: "14px" }}>
                <strong>
                  Please wait. AI Agent is analyzing the interrogation response – this may
                  take about a minute depending on the complexity of the complaint.
                </strong>
              </p>
            </div>
          </div>

        </form>

        {errorMessage && (
          <div className="popup-overlay">
            <div className="popup-container">
              <h2>Error</h2>
              <div className="error-message" style={{ marginBottom: '15px', fontSize: '16px', color: '#333' }}>
                <strong>{errorMessage}</strong>
              </div>
              <button
                className="error-ok-button"
                onClick={() => setErrorMessage(null)}
                style={{ padding: '10px 20px', fontSize: '14px', color: 'white', backgroundColor: '#007bff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                OK
              </button>
            </div>
          </div>
        )}

        {showOtherLanguagePopup && (
          <div className="popup-overlay">
            <div className="popup-container">
              <h3>Support for more languages coming soon!</h3>
              <p>In the meantime, please translate the statement into English using tools like <a href="https://chat.openai.com/" target="_blank" rel="noopener noreferrer">ChatGPT</a> and <a href="https://openpdf.com/lp/translate-pdf.html" target="_blank" rel="noopener noreferrer">OpenPDF</a> and upload the translated FIR as a PDF.</p>
              <p><strong>For assistance, please email us at contact@legalapp.in.</strong></p>
              <div className="popup-buttons">
                <button
                  className="submit-popup-button"
                  onClick={handleLanguageChangePopup}
                >Close</button>
              </div>
            </div>
          </div>
        )}

        {showWordLimitPopup && (
          <div className="popup-overlay">
            <div className="popup-container">
              <h3>Word Limit Reached</h3>
              <p style={{ textAlign: "center" }}>
                The free version supports a statement of up to {MAX_WORD_COUNT} words. Please shorten the text to continue, or contact us for a paid plan with a higher limit.
              </p>
              <div className="popup-buttons">
                <button
                  className="submit-popup-button"
                  onClick={() => setShowWordLimitPopup(false)}
                >OK</button>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default UploadStatement;
